import React from "react";
import { css } from "@emotion/css";
import { Spacing } from "./Spacing";

interface ReactivateAccountModalProps {
  isOpen: boolean;
  onReactivate: () => void;
  onCancel: () => void;
  loading: boolean;
  error?: string | null;
}

export const ReactivateAccountModal: React.FC<ReactivateAccountModalProps> = ({
  isOpen,
  onReactivate,
  onCancel,
  loading,
  error,
}) => {
  if (!isOpen) return null;

  return (
    <div className={overlayStyle}>
      <div className={modalStyle}>
        <h2 className={titleStyle}>Account Deactivated</h2>
        <p className={messageStyle}>
          Your account has been deactivated. Would you like to reactivate it using your login credentials?
        </p>
        {error && (
          <>
            <Spacing size={20} />
            <div className={errorStyle}>{error}</div>
          </>
        )}
        <Spacing size={20} />
        <div className={buttonContainerStyle}>
          <button className={cancelButtonStyle} onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button 
            className={reactivateButtonStyle} 
            onClick={onReactivate} 
            disabled={loading}
          >
            {loading ? "Reactivating..." : "Reactivate Account"}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const modalStyle = css`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 32px;
  max-width: 480px;
  width: 90%;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const titleStyle = css`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 16px 0;
  text-align: center;
`;

const messageStyle = css`
  color: #adadad;
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
  margin: 0;
`;

const buttonContainerStyle = css`
  display: flex;
  gap: 16px;
  justify-content: center;
  align-items: center;
`;

const cancelButtonStyle = css`
  background: transparent;
  color: #adadad;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const reactivateButtonStyle = css`
  background: #0f7dff;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: #449bff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const errorStyle = css`
  color: #f04251;
  font-size: 14px;
  text-align: center;
  background: rgba(240, 66, 81, 0.1);
  border: 1px solid rgba(240, 66, 81, 0.3);
  border-radius: 8px;
  padding: 12px;
`;
